import clsx from "clsx";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  ProgressCircle,
  Select,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import { isNullOrUndefined } from "@smart/itops-utils-basic";
import {
  useLoadMatterTypeCategories,
  useLoadSetting,
  useUpdateSetting,
} from "@smart/manage-gql-client-dom";

import { NotificationSetting } from "./notification";
import { AreaInfo } from "../types";

const GeneralSettings = () => {
  const { states, terms } = loadLocale();
  const providerInfo = useProviderInfo();
  const { staff } = useSmokeballApp();
  const { "LINK-2720": notificationSettings } = useFeatureFlags();
  const { result: setting, loading: loadingSetting } = useLoadSetting({});
  const categories = useLoadMatterTypeCategories({
    teamUri: setting?.teamUri,
  });
  const [update] = useUpdateSetting();

  const areasOfLaw = setting?.areasOfLaw?.map((area) => area.trim());
  const availableAreasOfLaw: AreaInfo[] | undefined = categories.result?.map(
    (cat) => ({ name: cat, displayName: cat }),
  );

  const selectedAreasOfLaw: AreaInfo[] =
    availableAreasOfLaw?.filter((cat) =>
      areasOfLaw?.includes(cat.name.trim()),
    ) || [];

  const selectedStates = states.filter((s) =>
    setting?.locations?.includes(s.name),
  );

  const staffToDisplay = staff?.current?.filter((s) => s.enabled) || [];

  if (loadingSetting || staff?.loading || categories.loading)
    return <ProgressCircle />;

  return (
    <div className="flex flex-col p-[2rem] gap-[6.4rem] h-full">
      <div className="flex-1 min-h-0 p-[0.4rem] overflow-y-auto">
        <div className="flex flex-col gap-medium">
          {notificationSettings && (
            <>
              <div className="font-semibold">Notify upon form completion:</div>
              <NotificationSetting
                savedNotificationSetting={{
                  savedStaffIds: staffToDisplay
                    .filter((s) =>
                      setting?.staffToNotify?.otherStaffIds?.includes(s.id),
                    )
                    .map((s) => s.id),
                  savedNotifyCreator: isNullOrUndefined(
                    setting?.staffToNotify?.creator,
                  )
                    ? true
                    : setting?.staffToNotify?.creator,
                  savedNotifyPersonResponsible:
                    !!setting?.staffToNotify?.personResponsible,
                  savedNotifyPersonAssisting:
                    !!setting?.staffToNotify?.personAssisting,
                }}
                staffToDisplay={staffToDisplay}
                localeTerms={terms}
                updateSettings={update}
              />
            </>
          )}
          <div
            className={clsx(
              "font-semibold",
              notificationSettings && "mt-[2.4rem]",
            )}
          >
            Configure practice areas
          </div>
          <Select
            id="states"
            label={terms.state.title}
            options={states.map((state) => ({
              label: state.displayName,
              value: state.name,
            }))}
            value={selectedStates.map((state) => state.name)}
            onChange={(selectedValues) => {
              const locations = Array.isArray(selectedValues)
                ? selectedValues
                : [selectedValues];
              update({
                variables: {
                  fields: { locations },
                },
              })?.catch(console.error);
            }}
            isMultiple
            placeholder={`Select ${terms.state.plural}`}
            dataTestId="states-select"
          />
          <Select
            id="practice-areas"
            label="Practice Areas"
            options={
              availableAreasOfLaw?.map((area) => ({
                label: area.displayName,
                value: area.name,
              })) || []
            }
            value={selectedAreasOfLaw.map((area) => area.name)}
            onChange={(selectedValues) => {
              const areasOfLawToUpdate = Array.isArray(selectedValues)
                ? selectedValues
                : [selectedValues];
              update({
                variables: {
                  fields: {
                    areasOfLaw: areasOfLawToUpdate.map((area) => area.trim()),
                  },
                },
              })?.catch(console.error);
            }}
            isMultiple
            placeholder="Select practice areas"
            dataTestId="practice-areas-select"
          />
        </div>
        <div className="flex flex-col gap-medium">
          <div className="flex items-center gap-3 mt-[4rem]">
            <Switch
              dataTestId="global-forms-toggle"
              toggled={!setting?.disableGlobalForms}
              onClick={() =>
                update({
                  variables: {
                    fields: {
                      disableGlobalForms: !setting?.disableGlobalForms,
                    },
                  },
                })?.catch(console.error)
              }
            />
            <span className="text-sm font-semibold text-gray-900">
              Show {providerInfo?.label || "Smokeball"} Forms
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-600">
              You can show or hide {providerInfo?.label || "Smokeball"}-provided
              forms to keep your list focused on the forms your firm uses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { GeneralSettings };
